import { Link } from "@/components/LangLink";
import { ArrowRight, ShieldCheck, Gem, Handshake } from "lucide-react";
import EditorialMarquee from "@/components/Marquee";
import { Reveal, Overline } from "@/components/Reveal";
import { IMAGES } from "@/data/site";
import { tr } from "@/i18n";

const STATS = [
  ["25+", "Global Destinations"],
  ["500+", "Private Relationships"],
  ["24/7", "Concierge-Led Support"],
  ["11", "Languages Spoken"],
];

const VALUES = [
  {
    icon: ShieldCheck,
    title: "Discretion",
    text: "Privacy is not a feature — it is the foundation. Every request is handled with absolute confidentiality.",
  },
  {
    icon: Gem,
    title: "Excellence",
    text: "Every detail is held to an uncompromising standard — the standard you expect and deserve.",
  },
  {
    icon: Handshake,
    title: "Trust",
    text: "A vetted global network of partners and advisors, built over years of relationships.",
  },
];

const CHAPTERS = [
  {
    id: "our-story",
    num: "01",
    title: "Our Story",
    image: "/images/ab-story.jpeg",
    paragraphs: [
      "Delux Concierge was founded in 2020 with a simple observation: for high-net-worth individuals, access to exceptional services is rarely the real challenge. Time is.",
      "Our founder, Mr Roberts, brought decades of experience within the luxury hospitality sector and recognised an opportunity to fundamentally improve the way premium services are accessed. Clients with demanding schedules do not simply require the highest standard of service; they require the right solution, sourced efficiently, delivered reliably and arranged within the timeframe they need.",
      "This insight became the foundation of Delux Concierge.",
      "We set out to create a concierge service that would act as a trusted bridge between our clients and the world's most sought-after services, experiences and specialists. Rather than simply presenting options, our role is to understand what the client needs, identify the most appropriate solution and execute it with precision — while remaining conscious of both quality and value.",
    ],
  },
  {
    id: "built-around-time",
    num: "02",
    title: "Built Around Time",
    image: "/images/ab-time.jpeg",
    paragraphs: [
      "Time is at the heart of everything we do.",
      "Our business model has been built around delivering exceptional solutions within highly efficient timeframes. Whether a client requires a private aircraft, yacht charter, luxury accommodation, chauffeur service, bespoke experience or a more complex lifestyle request, our objective remains the same: find the right solution and make it happen without unnecessary delay.",
      "Over the years, we have invested heavily in developing an international network of carefully selected partners across the luxury travel, hospitality, aviation, yachting, lifestyle and specialist services sectors. This network allows us to respond quickly, access trusted expertise across multiple markets and identify solutions that may otherwise take considerable time to source independently.",
      "Our ambition is simple: no request should be left unanswered simply because it is complex, time-sensitive or outside the ordinary.",
    ],
  },
  {
    id: "more-than-access",
    num: "03",
    title: "More Than Access",
    image: "/images/ab-access.jpeg",
    paragraphs: [
      "Access alone is not enough.",
      "We believe true concierge service is measured by what happens between the request and the result. That means understanding the brief properly, anticipating potential challenges, identifying the most suitable options, negotiating where appropriate and overseeing the details through to completion.",
      "Our clients trust us not simply to find a service, but to find the right service.",
      "That philosophy also shapes the way we work with our partners. We have built our network around relationships with established and trusted specialists who share our expectations for service, reliability and execution. By combining these relationships with our understanding of the luxury market, we are able to pursue solutions that balance quality, efficiency and competitive value without compromising the experience.",
    ],
  },
  {
    id: "our-philosophy",
    num: "04",
    title: "Our Philosophy",
    image: "/images/ab-philosophy.jpeg",
    paragraphs: [
      "We do not believe luxury should be complicated.",
      "Our role is to remove the time, uncertainty and administrative burden that can come with arranging exceptional services, allowing our clients to focus on what matters to them.",
      "From the simplest request to the most complex arrangement, we approach every brief with the same principles: understand the requirement, move quickly, source intelligently and execute to an exceptional standard.",
      "Because ultimately, our clients are not simply investing in a service. They are investing in their time. And that is the philosophy upon which Delux Concierge was built.",
    ],
  },
];

const Chapter = ({ c, index }) => {
  const ivory = index % 2 === 0;
  return (
    <section data-testid={`about-chapter-${c.id}`} className={ivory ? "bg-ivory text-navy" : "bg-ink text-white"}>
      <div className="max-w-6xl mx-auto px-6 lg:px-12 py-24 lg:py-32 grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20 items-center">
        <Reveal className={index % 2 === 1 ? "lg:order-2" : ""}>
          <div className="aspect-[3/4] overflow-hidden">
            <img src={c.image} alt={tr(c.title)} className="w-full h-full object-cover" />
          </div>
        </Reveal>
        <Reveal delay={0.15} className={index % 2 === 1 ? "lg:order-1" : ""}>
          <div className="flex items-baseline gap-5">
            <span className={`font-serif italic text-lg ${ivory ? "text-navy/40" : "text-gold"}`}>{c.num}</span>
            <h2 className="font-serif font-light text-4xl lg:text-5xl">{tr(c.title)}</h2>
          </div>
          <div className={`mt-8 space-y-5 font-light leading-relaxed ${ivory ? "text-navy/70" : "text-white/60"}`}>
            {c.paragraphs.map((p, i) => (
              <p key={i} className={i === 0 ? "text-lg" : "text-[0.95rem]"}>
                {tr(p)}
              </p>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default function About() {
  return (
    <div data-testid="about-page">
      {/* ——— SPLIT STORY HERO ——— */}
      <section className="grid grid-cols-1 lg:grid-cols-2">
        <div className="relative min-h-[55vh] lg:min-h-screen">
          <img src="/images/ev-4.jpeg" alt={tr("Grand celebration venue")} className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent lg:bg-gradient-to-r" />
        </div>
        <div className="flex items-center bg-ink px-6 lg:px-16 xl:px-24 pt-36 pb-24 lg:py-40">
          <div className="max-w-xl">
            <Overline>{tr("About Delux Concierge")}</Overline>
            <h1 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mt-6">{tr("Born from a simple")} <em className="gold-shimmer italic">{tr("belief.")}</em>
            </h1>
            <p className="mt-9 text-white/60 font-light leading-relaxed">{tr("Delux Concierge was founded on the belief that exceptional living should feel effortless rather than arranged — a relationship, not a transaction.")}</p>
            <p className="mt-6 text-white/60 font-light leading-relaxed">{tr("We do not measure ourselves by the number of requests we fulfil, but by the quality of the outcomes we help our clients achieve.")}</p>
          </div>
        </div>
      </section>

      {/* ——— CHAPTERS ——— */}
      {CHAPTERS.map((c, i) => (
        <Chapter key={c.id} c={c} index={i} />
      ))}

      {/* ——— STATS BAND ——— */}
      <section className="bg-ivory text-navy border-t border-navy/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16 lg:py-24 grid grid-cols-2 lg:grid-cols-4 gap-10 text-center">
          {STATS.map(([n, l], i) => (
            <Reveal key={l} delay={i * 0.08}>
              <p className="font-serif font-light text-5xl lg:text-6xl text-gold">{n}</p>
              <p className="mt-3 text-[0.62rem] uppercase tracking-[0.3em] text-navy/55">{tr(l)}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ——— VALUES ——— */}
      <section className="py-24 lg:py-36">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <Reveal>
            <Overline>{tr("What We Stand For")}</Overline>
            <h2 className="font-serif font-light text-4xl sm:text-5xl mt-6 max-w-2xl">{tr("Values that shape every relationship.")}</h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 mt-16 border border-white/10">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.1} className="bg-ink p-10 lg:p-12">
                <v.icon strokeWidth={1} size={26} className="text-gold mb-8" />
                <h3 className="font-serif text-2xl font-light mb-4">{tr(v.title)}</h3>
                <p className="text-white/50 font-light text-sm leading-relaxed">{tr(v.text)}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <EditorialMarquee />

      {/* ——— CTA ——— */}
      <section className="py-28 text-center">
        <Reveal>
          <Overline className="mb-6">{tr("Begin")}</Overline>
          <h2 className="font-serif font-light text-4xl sm:text-5xl">{tr("Your life,")}<em className="gold-shimmer italic">{tr("at its very best.")}</em>
          </h2>
          <Link
            to="/contact"
            data-testid="about-request-service-cta"
            className="group inline-flex items-center gap-3 mt-12 bg-gold text-black text-xs uppercase tracking-[0.3em] px-10 py-4 hover:bg-gold-bright transition-colors duration-400"
          >{tr("Request Service")}<ArrowRight strokeWidth={1.5} size={15} className="transition-transform duration-400 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </section>
    </div>
  );
}
